import React, { useState, useEffect } from "react";
import {
    Box, SpaceBetween, StatusIndicator, Header, Table, ExpandableSection, Badge, Link
} from "@amzn/awsui-components-react";
import { getDocumentFromVault } from 'src/Ateam-BI/Vault/VaultAPIs';
import { VAULT_SERVICES } from 'src/Ateam-BI/Vault/constants';
import DownloadTieOutResultsButton from "./DownloadTieOutResultsButton";


// Helper to render status emoji/symbols as StatusIndicator or Badge
const StatusCell = ({ status }) => {
    if (!status) return <Box>—</Box>;
    const s = status.trim();
    if (s === '✅' || s.startsWith('✅')) return <StatusIndicator type="success">PASS</StatusIndicator>;
    if (s === '⚠️' || s.startsWith('⚠️')) return <StatusIndicator type="warning">ROUND</StatusIndicator>;
    if (s === '❌' || s.startsWith('❌')) return <StatusIndicator type="error">WARN</StatusIndicator>;
    if (s === 'ℹ️' || s.startsWith('ℹ️')) return <StatusIndicator type="info">INFO</StatusIndicator>;
    if (s === '^') return <StatusIndicator type="success">PASS ^</StatusIndicator>;
    if (s === '<') return <StatusIndicator type="success">PASS</StatusIndicator>;
    return <Box>{s}</Box>;
};

// Tie Out Legend section
const TieOutLegend = () => (
    <ExpandableSection headerText="Tie Out Legend" defaultExpanded={false} variant="container">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px 32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <StatusIndicator type="success">PASS</StatusIndicator>
                <Box variant="small" color="text-body-secondary">Amounts agree, no issues</Box>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <StatusIndicator type="warning">ROUND</StatusIndicator>
                <Box variant="small" color="text-body-secondary">This is a deliberately rounded value</Box>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <StatusIndicator type="error">WARN</StatusIndicator>
                <Box variant="small" color="text-body-secondary">Important issue requiring attention</Box>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Badge color="blue">^</Badge>
                <Box variant="small" color="text-body-secondary">Figures foot (vertical sum agrees)</Box>
            </div>
        </div>
    </ExpandableSection>
);

// Files Provided section
const FilesProvidedSection = ({ filesData }) => {
    if (!filesData) return null;

    // Render file entry - only as a link if there's a valid URL, otherwise plain text
    const renderFileEntry = (file, index) => {
        // file can be a string (filename) or an object with { name, url }
        if (typeof file === 'object' && file !== null) {
            const name = file.name || file.filename || file.label || String(file);
            const url = file.url || file.href || file.link || null;
            if (url && url !== '#') {
                return <Link key={index} href={url} variant="secondary" external>{name}</Link>;
            }
            return <Box key={index} variant="p">{name}</Box>;
        }
        // Plain string filename - no link available
        return <Box key={index} variant="p">{file}</Box>;
    };

    return (
        <ExpandableSection headerText="Files Provided" defaultExpanded={false} variant="container">
            <SpaceBetween direction="vertical" size="m">
                <div>
                    <SpaceBetween direction="vertical" size="xxs">
                        {(filesData.uploaded_files || []).map((f, i) => renderFileEntry(f, i))}
                    </SpaceBetween>
                </div>
                <div>
                    <Box variant="awsui-key-label" padding={{ bottom: 'xxs' }}>Supporting Deliverables (per mapping)</Box>
                    <SpaceBetween direction="horizontal" size="xs">
                        {(filesData.supporting_deliverables || []).map((f, i) => (
                            <StatusIndicator key={i} type="success">{f} — Available</StatusIndicator>
                        ))}
                    </SpaceBetween>
                </div>
                {filesData.cy_page_reference && (
                    <Box><strong>CY Financial Statements page number:</strong> {filesData.cy_page_reference}</Box>
                )}
            </SpaceBetween>
        </ExpandableSection>
    );
};

// Identified Issues section
const IdentifiedIssuesSection = ({ issuesData }) => {
    if (!issuesData) return null;
    const { table, no_issues_message } = issuesData;

    const issueCount = table?.length || 0;
    const headerText = `Identified Issues (${issueCount})`;

    if (no_issues_message || !table || table.length === 0) {
        return (
            <ExpandableSection headerText={headerText} defaultExpanded={true} variant="container">
                <StatusIndicator type="success">{no_issues_message || 'No issues identified.'}</StatusIndicator>
            </ExpandableSection>
        );
    }

    return (
        <ExpandableSection headerText={headerText} defaultExpanded={true} variant="container">
            <Table
                columnDefinitions={[
                    { id: "no", header: "No.", cell: (item) => item.no, width: 60 },
                    { id: "table_number", header: "Table No.", cell: (item) => item.table_number, width: 90 },
                    { id: "table_name", header: "Table Name", cell: (item) => item.table_name, width: 160 },
                    { id: "fs_reference", header: "FS Reference", cell: (item) => item.fs_reference, width: 170 },
                    { id: "category", header: "Category", cell: (item) => item.category, width: 100 },
                    { id: "issue_type", header: "Issue Type", cell: (item) => item.issue_type, width: 110 },
                    { id: "current_format", header: "Current Format", cell: (item) => (
                        <Box color="text-status-error">{item.current_format}</Box>
                    ), width: 130 },
                    { id: "required_format", header: "Required Format", cell: (item) => item.required_format, width: 155 },
                    { id: "status", header: "Status", cell: (item) => <StatusCell status={item.status} />, width: 110 },
                    { id: "action_required", header: "Action Required", cell: (item) => item.action_required, width: 150 },
                ]}
                items={table}
                variant="embedded"
                wrapLines
            />
        </ExpandableSection>
    );
};

// Table 1: Prior Period Validation
const PriorPeriodSection = ({ tableData }) => {
    if (!tableData) return null;
    const { pre_check, table, no_data_message } = tableData;

    const description = pre_check
        ? `PRE-CHECK: ${pre_check} — Comparative period validated against prior year financials.`
        : undefined;

    if (no_data_message) {
        return (
            <ExpandableSection
                headerText="Table 1 — Validation of Prior Period Figures"
                headerDescription={description}
                defaultExpanded={true}
                variant="container"
            >
                <StatusIndicator type="info">{no_data_message}</StatusIndicator>
            </ExpandableSection>
        );
    }

    return (
        <ExpandableSection
            headerText="Table 1 — Validation of Prior Period Figures"
            headerDescription={description}
            defaultExpanded={true}
            variant="container"
        >
            <Table
                columnDefinitions={[
                    { id: "line_item", header: "Line Item", cell: (item) => item.line_item, width: 300 },
                    { id: "py_figure", header: "PY Figure Presented", cell: (item) => (
                        <Box textAlign="right">{item.py_figure_presented}</Box>
                    ), width: 180 },
                    { id: "as_per_py", header: "As per PY Financials", cell: (item) => (
                        <Box textAlign="right">{item.as_per_py_financials}</Box>
                    ), width: 180 },
                    { id: "status", header: "Validation Status", cell: (item) => <StatusCell status={item.status} />, width: 140 },
                    { id: "reference", header: "Reference", cell: (item) => (
                        <Box fontSize="body-s" color="text-body-secondary">{item.reference}</Box>
                    ), width: 140 },
                    { id: "remarks", header: "Remarks", cell: (item) => (
                        <Box color="text-body-secondary">{item.remarks || '—'}</Box>
                    ), width: 160 },
                ]}
                items={table || []}
                variant="embedded"
                stripedRows
                wrapLines
            />
        </ExpandableSection>
    );
};

// Table 2: Mathematical Accuracy
const MathAccuracySection = ({ tableData }) => {
    if (!tableData) return null;
    const { table, no_data_message } = tableData;

    if (no_data_message) {
        return (
            <ExpandableSection
                headerText="Table 2 — Mathematical Accuracy of the CY Financial Statements"
                defaultExpanded={true}
                variant="container"
            >
                <StatusIndicator type="info">{no_data_message}</StatusIndicator>
            </ExpandableSection>
        );
    }

    return (
        <ExpandableSection
            headerText="Table 2 — Mathematical Accuracy of the CY Financial Statements"
            defaultExpanded={true}
            variant="container"
        >
            <Table
                columnDefinitions={[
                    { id: "check", header: "Check Performed", cell: (item) => item.check_performed, width: 220 },
                    { id: "calculation", header: "Calculation", cell: (item) => (
                        <Box fontSize="body-s" color="text-body-secondary">{item.calculation}</Box>
                    ), width: 340 },
                    { id: "expected", header: "Expected", cell: (item) => (
                        <Box textAlign="right">{item.expected}</Box>
                    ), width: 110 },
                    { id: "per_cy", header: "Per CY Financial Statements", cell: (item) => (
                        <Box textAlign="right">{item.per_cy_financial_statements}</Box>
                    ), width: 170 },
                    { id: "status", header: "Status", cell: (item) => <StatusCell status={item.status} />, width: 120 },
                    { id: "remarks", header: "Remarks", cell: (item) => (
                        <Box color="text-body-secondary">{item.remarks || '—'}</Box>
                    ), width: 120 },
                ]}
                items={table || []}
                variant="embedded"
                stripedRows
                wrapLines
            />
        </ExpandableSection>
    );
};

// Table 3: CY vs Supporting Working File Cross-Reference
const CYvsSupportingSection = ({ tableData }) => {
    if (!tableData) return null;
    const { table, no_data_message } = tableData;

    if (no_data_message) {
        return (
            <ExpandableSection
                headerText="Table 3 — Supporting Working File Cross-Reference"
                defaultExpanded={true}
                variant="container"
            >
                <StatusIndicator type="info">{no_data_message}</StatusIndicator>
            </ExpandableSection>
        );
    }

    return (
        <ExpandableSection
            headerText="Table 3 — Supporting Working File Cross-Reference"
            defaultExpanded={true}
            variant="container"
        >
            <Table
                columnDefinitions={[
                    { id: "line_item", header: "Line Item", cell: (item) => item.line_item, width: 270 },
                    { id: "fs_amount_cy", header: "FS Amount (CY)", cell: (item) => (
                        <Box textAlign="right">{item.fs_amount_cy}</Box>
                    ), width: 130 },
                    { id: "supporting_cy", header: "Supporting Working File (CY)", cell: (item) => item.supporting_working_file_cy, width: 140 },
                    { id: "supporting_rounded", header: "Working File — Rounded", cell: (item) => (
                        <Box textAlign="right">{item.supporting_working_file_rounded}</Box>
                    ), width: 140 },
                    { id: "cy_vs_supporting", header: "CY vs Supporting Working File", cell: (item) => {
                        const val = item.cy_vs_supporting;
                        if (!val || val === '$0' || val === '0' || val === 'N/A') {
                            return <Box textAlign="right" color="text-body-secondary">{val || '—'}</Box>;
                        }
                        return <Box textAlign="right" color="text-status-warning">{val}</Box>;
                    }, width: 170 },
                    { id: "status", header: "Status", cell: (item) => <StatusCell status={item.status} />, width: 100 },
                    { id: "reference", header: "Reference", cell: (item) => (
                        <Box fontSize="body-s" color="text-body-secondary">{item.reference}</Box>
                    ), width: 100 },
                    { id: "remarks", header: "Remarks", cell: (item) => {
                        const remarks = item.remarks;
                        if (!remarks || remarks === '—') return <Box color="text-body-secondary">—</Box>;
                        // Check if it's a warning remark
                        if (remarks.toLowerCase().includes('round') || remarks.toLowerCase().includes('tolerance')) {
                            return <Box fontSize="body-s" color="text-status-warning">{remarks}</Box>;
                        }
                        return <Box color="text-body-secondary">{remarks}</Box>;
                    }, width: 210 },
                ]}
                items={table || []}
                variant="embedded"
                stripedRows
                wrapLines
            />
        </ExpandableSection>
    );
};

// Annotated Financial Statement section - displays PDF as static image if available, falls back to JSON-based rendering
const AnnotatedFinancialStatementSection = ({ annotations, sectionName, filesProvided, annotatedPdfUrl }) => {
    // If we have a PDF, render it as a static (non-interactive) embedded image
    if (annotatedPdfUrl) {
        const pageRef = filesProvided?.cy_page_reference || '';
        // Append parameters to disable PDF viewer toolbar/navigation for a cleaner static look
        const pdfSrc = `${annotatedPdfUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`;
        return (
            <ExpandableSection
                headerText={`Annotated Financial Statement — ${pageRef || 'PDF'}`}
                headerDescription="Annotated PDF document with tie-out markings (static preview)"
                defaultExpanded={true}
                variant="container"
            >
                <div style={{
                    width: '100%',
                    border: '1px solid #d5dbdb',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    backgroundColor: '#f9f9f9',
                }}>
                    <iframe
                        src={pdfSrc}
                        style={{
                            width: '100%',
                            height: '900px',
                            border: 'none',
                            display: 'block',
                        }}
                        title={`Annotated Financial Statement - ${sectionName || ''}`}
                    />
                </div>
            </ExpandableSection>
        );
    }

    // Fall back to JSON-based annotation rendering if no PDF available
    if (!annotations || annotations.length === 0) return null;

    // Group annotations by page
    const annotationsByPage = annotations.reduce((acc, ann) => {
        const page = ann.page || 1;
        if (!acc[page]) acc[page] = [];
        acc[page].push(ann);
        return acc;
    }, {});

    // Build badge for a symbol/reference
    const renderBadge = (symbol, reference) => {
        if (symbol === '✓' || symbol === '✔') {
            if (reference === 'prior_year_same_quarter') return <Badge color="blue">PY</Badge>;
            if (reference === 'self-check') return <Badge color="green">✓</Badge>;
            return <Badge color="blue">PY</Badge>;
        }
        if (symbol === '^') return <Badge color="blue">^</Badge>;
        if (symbol === '<') {
            if (reference && reference !== 'prior_year_same_quarter') return <Badge color="green">{reference}</Badge>;
            return <Badge color="green">&lt;</Badge>;
        }
        return <Badge color="grey">{symbol}</Badge>;
    };

    const pages = Object.keys(annotationsByPage).sort((a, b) => Number(a) - Number(b));
    const pageRef = filesProvided?.cy_page_reference || '';

    return (
        <ExpandableSection
            headerText={`Annotated Financial Statement — ${pageRef || `p.${pages[0]}`}`}
            headerDescription={`Source document ${pageRef || `p.${pages[0]}`} with tie-out annotations. PY = prior year validated · F5 / N6.6 = working file cross-referenced · (R) = rounded · ^ = footing verified`}
            defaultExpanded={true}
            variant="container"
        >
            <div style={{ overflowX: 'auto' }}>
                {/* Document header - mimics the financial statement title */}
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                    <Box fontWeight="bold">AMAZON.COM, INC.</Box>
                    <Box fontWeight="bold">{(sectionName || '').toUpperCase()}</Box>
                    <Box fontSize="body-s" color="text-body-secondary">(in millions, except per share data) (unaudited)</Box>
                </div>

                {/* Annotation table - styled like a financial statement */}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '52%', padding: '4px 8px' }}></th>
                            <th colSpan="2" style={{ textAlign: 'center', padding: '4px 8px', borderBottom: '1px solid #d5dbdb', fontSize: '12px', fontWeight: 'bold' }}>
                                Annotated Values
                            </th>
                        </tr>
                        <tr>
                            <th style={{ padding: '4px 8px' }}></th>
                            <th style={{ textAlign: 'right', padding: '4px 8px', borderBottom: '2px solid #414d5c', fontSize: '12px', fontWeight: 'bold', width: '24%' }}>
                                Prior Year
                            </th>
                            <th style={{ textAlign: 'right', padding: '4px 8px 4px 28px', borderBottom: '2px solid #414d5c', fontSize: '12px', fontWeight: 'bold', width: '24%' }}>
                                Current Year
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {pages.map((page) => {
                            const pageAnns = annotationsByPage[page];

                            // Separate PY annotations from CY annotations
                            const pyAnnotations = pageAnns.filter(a => a.supporting_file_reference === 'prior_year_same_quarter');
                            const cyAnnotations = pageAnns.filter(a => a.supporting_file_reference !== 'prior_year_same_quarter');

                            // Get unique line items from CY annotations (they represent the financial statement lines)
                            const lineItems = [];
                            const seen = new Set();

                            // Use CY annotations as the primary rows (these reference supporting files)
                            cyAnnotations.forEach(ann => {
                                const key = ann.search_text;
                                if (!seen.has(key)) {
                                    seen.add(key);
                                    // Find matching PY annotation
                                    const pyMatch = pyAnnotations.find(py => py.search_text === key);
                                    lineItems.push({
                                        value: ann.search_text,
                                        cyAnnotation: ann,
                                        pyAnnotation: pyMatch,
                                        isTotalRow: ann.symbol === '^' || ann.supporting_file_reference?.includes('Total') || ann.supporting_file_reference?.includes('income') || ann.supporting_file_reference?.includes('Net income'),
                                    });
                                }
                            });

                            // Also add PY-only items that weren't in CY
                            pyAnnotations.forEach(ann => {
                                if (!seen.has(ann.search_text)) {
                                    seen.add(ann.search_text);
                                    lineItems.push({
                                        value: ann.search_text,
                                        cyAnnotation: null,
                                        pyAnnotation: ann,
                                        isTotalRow: false,
                                    });
                                }
                            });

                            return lineItems.map((item, idx) => {
                                const isTotal = item.isTotalRow;
                                const bgColor = isTotal ? '#f4f6f8' : 'transparent';
                                const borderTop = isTotal ? '1px solid #d5dbdb' : 'none';

                                return (
                                    <tr key={`${page}-${idx}`} style={{ borderBottom: 'none', backgroundColor: bgColor }}>
                                        <td style={{ padding: '4px 8px', fontWeight: isTotal ? 'bold' : 'normal', borderTop }}>
                                            {item.cyAnnotation?.supporting_file_reference || item.value}
                                        </td>
                                        <td style={{ padding: '4px 8px', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: isTotal ? 'bold' : 'normal', borderTop }}>
                                            {item.pyAnnotation && (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                                                    <span>{item.pyAnnotation.search_text}</span>
                                                    {renderBadge(item.pyAnnotation.symbol, item.pyAnnotation.supporting_file_reference)}
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ padding: '4px 8px 4px 28px', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: isTotal ? 'bold' : 'normal', borderTop }}>
                                            {item.cyAnnotation && (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                                                    <span>{item.cyAnnotation.search_text}</span>
                                                    {renderBadge(item.cyAnnotation.symbol, item.cyAnnotation.supporting_file_reference)}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            });
                        })}
                    </tbody>
                </table>

                <Box padding={{ top: 's' }} fontSize="body-s" color="text-body-secondary">
                    See accompanying notes to consolidated financial statements.
                </Box>
            </div>
        </ExpandableSection>
    );
};

// Table 4: Textual Disclosure Changes
const TextualDisclosureSection = ({ tableData }) => {
    if (!tableData) return null;
    const { table, no_data_message } = tableData;

    if (no_data_message || !table || table.length === 0) {
        return (
            <ExpandableSection
                headerText="Table 4 — Textual Disclosure Changes"
                defaultExpanded={true}
                variant="container"
            >
                <StatusIndicator type="info">{no_data_message || 'No textual disclosures found.'}</StatusIndicator>
            </ExpandableSection>
        );
    }

    return (
        <ExpandableSection
            headerText="Table 4 — Textual Disclosure Changes"
            defaultExpanded={true}
            variant="container"
        >
            <Table
                columnDefinitions={[
                    { id: "disclosure_item", header: "Disclosure Item", cell: (item) => item.disclosure_item || item.disclosure || item.text, width: 200 },
                    { id: "cy_text_summary", header: "CY Text Summary", cell: (item) => item.cy_text_summary, width: 250 },
                    { id: "py_text_summary", header: "PY Text Summary", cell: (item) => item.py_text_summary, width: 250 },
                    { id: "change_detected", header: "Change Detected", cell: (item) => item.change_detected, width: 100 },
                    { id: "status", header: "Status", cell: (item) => <StatusCell status={item.status} />, width: 100 },
                    { id: "remarks", header: "Remarks", cell: (item) => (
                        <Box color="text-body-secondary">{item.remarks || '—'}</Box>
                    ), width: 200 },
                ]}
                items={table || []}
                variant="embedded"
                stripedRows
                wrapLines
            />
        </ExpandableSection>
    );
};


/**
 * Component to fetch and display tie-out results from S3.
 * Renders structured tables from the JSON results file with collapsible sections.
 * Matches the presentation from the example HTML template.
 */
const TieOutResultContent = ({ data }) => {
    const [loading, setLoading] = useState(true);
    const [resultData, setResultData] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [annotatedPdfUrl, setAnnotatedPdfUrl] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchResultContent();
    }, [data]);

    const loadS3File = async (s3Path) => {
        const withoutProtocol = s3Path.replace(/^s3:\/\//, '');
        const firstSlashIndex = withoutProtocol.indexOf('/');
        const filePath = withoutProtocol.substring(firstSlashIndex + 1);
        const arr = filePath.split('.');
        const contentType = arr[arr.length - 1];
        const document = await getDocumentFromVault(contentType, VAULT_SERVICES.DOCEVAL, { "file_path": filePath });
        return { fileUrl: document?.fileUrl, contentType };
    };

    const fetchResultContent = async () => {
        setLoading(true);
        setError(null);
        setResultData(null);
        setPdfUrl(null);
        setAnnotatedPdfUrl(null);

        try {
            const responseS3Path = data?.response_s3_path;
            const annotatedPdfS3Path = data?.annotated_pdf_s3_path;

            if (responseS3Path) {
                const { fileUrl } = await loadS3File(responseS3Path);
                if (fileUrl) {
                    const response = await fetch(fileUrl);
                    if (!response.ok) {
                        throw new Error(`Failed to fetch results: ${response.status}`);
                    }
                    const text = await response.text();
                    try {
                        const jsonData = JSON.parse(text);
                        setResultData(jsonData);
                    } catch {
                        setResultData(text);
                    }
                }

                // Also try to load the annotated PDF for inline display
                if (annotatedPdfS3Path) {
                    try {
                        const { fileUrl: pdfFileUrl } = await loadS3File(annotatedPdfS3Path);
                        if (pdfFileUrl) {
                            setAnnotatedPdfUrl(pdfFileUrl);
                        }
                    } catch (pdfErr) {
                        // PDF loading failed silently - will fall back to JSON rendering
                        console.warn('Could not load annotated PDF, falling back to JSON rendering:', pdfErr);
                    }
                }
            } else if (annotatedPdfS3Path) {
                const { fileUrl } = await loadS3File(annotatedPdfS3Path);
                if (fileUrl) {
                    setPdfUrl(fileUrl);
                }
            } else {
                setError('No results available. The tie-out may not have been run yet.');
            }
        } catch (e) {
            console.error('Error fetching tie-out results:', e);
            setError('Failed to load results. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box padding="l">
                <StatusIndicator type="loading">Loading results...</StatusIndicator>
            </Box>
        );
    }

    if (error) {
        return (
            <Box padding="l">
                <SpaceBetween direction="vertical" size="m">
                    <Box float="right">
                        <DownloadTieOutResultsButton data={data} />
                    </Box>
                    <StatusIndicator type="info">{error}</StatusIndicator>
                </SpaceBetween>
            </Box>
        );
    }

    if (pdfUrl) {
        return (
            <Box padding="l">
                <SpaceBetween direction="vertical" size="m">
                    <Box float="right">
                        <DownloadTieOutResultsButton data={data} />
                    </Box>
                    <iframe
                        src={pdfUrl}
                        style={{ width: '100%', height: '700px', border: '1px solid #d5dbdb', borderRadius: '4px' }}
                        title={`Annotated PDF - ${data?.section || ''}`}
                    />
                </SpaceBetween>
            </Box>
        );
    }

    if (resultData && typeof resultData === 'object') {
        return (
            <Box padding="l">
                <SpaceBetween direction="vertical" size="l">
                    {/* Section header with download button on the same line */}
                    <Header
                        variant="h3"
                        actions={<DownloadTieOutResultsButton data={data} />}
                    >
                        {resultData.section_name || data?.section || 'Tie-Out Results'}
                    </Header>

                    {/* Files Provided - collapsible */}
                    <FilesProvidedSection filesData={resultData.files_provided} />

                    {/* Tie Out Legend - collapsible */}
                    <TieOutLegend />

                    {/* Identified Issues - collapsible, shown before tables */}
                    <IdentifiedIssuesSection issuesData={resultData.identified_issues} />

                    {/* Annotated Financial Statement - shows PDF if available, falls back to JSON */}
                    <AnnotatedFinancialStatementSection
                        annotations={resultData.annotations}
                        sectionName={resultData.section_name}
                        filesProvided={resultData.files_provided}
                        annotatedPdfUrl={annotatedPdfUrl}
                    />
                    
                    {/* Table 1: Prior Period Validation - collapsible */}
                    <PriorPeriodSection tableData={resultData.table_1_prior_period_validation} />

                    {/* Table 2: Mathematical Accuracy - collapsible */}
                    <MathAccuracySection tableData={resultData.table_2_mathematical_accuracy} />

                    {/* Table 3: Supporting Working File Cross-Reference - collapsible */}
                    <CYvsSupportingSection tableData={resultData.table_3_cy_vs_supporting} />

                    {/* Table 4: Textual Disclosure Changes - collapsible */}
                    <TextualDisclosureSection tableData={resultData.table_4_textual_disclosure} />
                </SpaceBetween>
            </Box>
        );
    }

    if (resultData && typeof resultData === 'string') {
        return (
            <Box padding="l">
                <SpaceBetween direction="vertical" size="m">
                    <Box float="right">
                        <DownloadTieOutResultsButton data={data} />
                    </Box>
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{resultData}</pre>
                </SpaceBetween>
            </Box>
        );
    }

    return (
        <Box padding="l">
            <StatusIndicator type="info">No results to display.</StatusIndicator>
        </Box>
    );
};

export default TieOutResultContent;
